import { applyPriceOverride, computeAreaBreakdown, getMaterialListPrice, hasPriceOverride } from "../../../helpers/estimateLinePricing";
import { computeLabourLineTotal, computeLabourSqft, computeMaterialLineTotal, formatCurrency } from "../../../helpers/estimateTotals";
import { formatDeductionType, formatMeasurementDimensionsWithUnits, formatSurfaceType } from "../../../helpers/measurementModel";
import { LABOUR_PRICING_METHOD } from "../../../helpers/estimates";

/** Short note beside the Adjusted pill: "+10%" or "$65.00 → $100.00". */
export function formatOverrideNote(base, row) {
  if (!hasPriceOverride(row)) return "";
  const value = Number(row.price_override_value);
  if (row.price_override_mode === "percent") {
    const magnitude = Math.abs(value);
    const text = Number.isInteger(magnitude) ? String(magnitude) : String(Math.round(magnitude * 10) / 10);
    if (value > 0) return `+${text}%`;
    if (value < 0) return `−${text}%`;
    return "0%";
  }
  const next = applyPriceOverride(base, row.price_override_mode, row.price_override_value);
  return `${formatCurrency(base)} → ${formatCurrency(next)}`;
}
export function featureDetail(workType, materialsCatalog = []) {
  const areas = computeAreaBreakdown(workType);
  const deductions = (workType?.sections || []).filter((section) => section.record_kind === "deduction");
  const measurements = areas.map((area) => ({
    id: area.section.section_id,
    name: area.section.name || formatSurfaceType(area.section.surface_type),
    dimensions: formatMeasurementDimensionsWithUnits(area.section),
    gross: area.gross,
    net: area.net,
    deductions: area.deductions.map((item) => ({
      id: item.section_id,
      name: formatDeductionType(item.deduction_type),
      dimensions: formatMeasurementDimensionsWithUnits(item),
      sqft: Number(item.area_sqft) || 0,
    })),
    legacyShare: area.legacyShare,
    surface: formatSurfaceType(area.section.surface_type),
  }));
  const unlinked = deductions.filter((item) => !areas.some((area) => area.deductions.includes(item)));

  const materials = (workType?.materials || []).map((row) => ({
    id: row.room_material_id,
    name: row.material_name_snapshot || "Material",
    quantity: row.quantity,
    unit: row.unit_of_measure_snapshot || "",
    unitPrice: getMaterialListPrice(row, materialsCatalog),
    adjusted: hasPriceOverride(row),
    adjustment: formatOverrideNote(getMaterialListPrice(row, materialsCatalog), row),
    total: computeMaterialLineTotal(row, materialsCatalog),
  }));

  const labour = (workType?.labour || []).map((row) => {
    const perSqft = (row.pricing_method || LABOUR_PRICING_METHOD.HOURLY) === LABOUR_PRICING_METHOD.SQUARE_FOOT;
    const ids = Array.isArray(row.surface_section_ids) ? row.surface_section_ids.filter(Boolean) : [];
    const surfaces = ids.length
      ? areas.filter((area) => ids.includes(area.section.section_id)).map((area) => area.section.name || formatSurfaceType(area.section.surface_type))
      : [];
    return {
      id: row.labour_id,
      name: row.labour_activity || "Labour",
      quantity: perSqft ? computeLabourSqft(row, workType) : Number(row.estimated_hours) || 0,
      unit: perSqft ? "ft²" : "hr",
      rate: row.hourly_rate,
      adjusted: hasPriceOverride(row),
      adjustment: formatOverrideNote(Number(row.hourly_rate) || 0, row),
      total: computeLabourLineTotal(row, workType),
      surfaces: perSqft ? (surfaces.length ? surfaces : ["All surfaces"]) : [],
    };
  });

  return { measurements, unlinked, materials, labour };
}

export function money(value) {
  return formatCurrency(value);
}
